import { type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { ChartCard } from "./chart-card";

/** Bảng màu trạng thái dùng chung cho các donut ở tab Tổng quan. */
export const STATUS_COLORS = {
	done: "#16a34a",
	pending: "#f59e0b",
	idle: "#d1d5db",
	// Text entries are separate from the fills above: a colour that reads as a
	// donut segment does not necessarily read as a numeral — amber-500 measures
	// 2.15 on white, under the 3.0 large text is allowed. Both resolve through
	// CSS variables so they track the theme; no single literal works on both
	// grounds, since amber-700 gives 5.02 on white but 2.70 on a dark card and
	// amber-500 is the other way round.
	pendingText: "var(--chart-pending-text)",
	idleText: "hsl(var(--muted-foreground))",
	male: "#2563eb",
	female: "#ec4899",
} as const;

export interface DonutSegment {
	label: string;
	value: number;
	color: string;
	/**
	 * Màu của con số, khi màu vòng tròn quá nhạt để đọc thành chữ. Xám nhạt đủ
	 * để phân biệt một lát bánh nhưng gần như vô hình ở cỡ chữ lớn.
	 */
	textColor?: string;
	/** Dòng nhỏ dưới con số, ví dụ "chờ đến làm thủ tục". */
	hint?: string;
	/**
	 * `false` khi lát này chỉ có mặt để lấp đầy vòng tròn và con số của nó đã nằm
	 * ở một card khác — vẽ thêm một card nữa chỉ là lặp lại.
	 */
	showCard?: boolean;
}

export interface DonutChart {
	title: string;
	/** Tên file PNG của riêng vòng tròn này. Kebab-case, không dấu. */
	filename: string;
	/**
	 * Mẫu số mà mọi tỉ lệ của vòng tròn này lấy theo. `showCard: false` khi mẫu số
	 * đã có sẵn ở một card khác — lặp lại đúng con số đó chỉ làm lưới dài thêm.
	 */
	total: { label: string; value: number; hint?: string; showCard?: boolean };
	segments: DonutSegment[];
	/** Phần được nêu ở giữa vòng tròn. Mặc định là phần đầu tiên. */
	highlightIndex?: number;
	/**
	 * Con số ở giữa vòng tròn, khi điều đáng nói là một nhóm GỘP nhiều lát chứ
	 * không phải một lát riêng lẻ. Được ưu tiên hơn `highlightIndex`.
	 */
	highlight?: { label: string; value: number };
}

interface DonutBreakdownProps {
	description?: ReactNode;
	charts: DonutChart[];
}

const formatShare = (value: number, total: number) =>
	total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0%";

/**
 * Một khối duy nhất: các con số nằm thành card bên trái, tỉ lệ nằm trong vòng
 * tròn bên phải.
 *
 * Người đọc cần cả hai — số tuyệt đối để báo cáo, tỉ lệ để thấy ngay bức tranh —
 * nên đặt cạnh nhau thay vì hai khối rời.
 *
 * Nút tải nằm ở TỪNG vòng tròn, không phải ở khối: ảnh cần dán vào báo cáo là
 * cái biểu đồ, không phải cả bảng số bên cạnh nó.
 */
export function DonutBreakdown({ description, charts }: DonutBreakdownProps) {
	return (
		<Card>
			<CardContent className="p-4 md:p-6">
				{description && <p className="mb-4 text-sm text-muted-foreground">{description}</p>}

				{/* Mỗi biểu đồ là MỘT hàng của lưới, không phải hai cột dựng song song:
				    số card mỗi biểu đồ mỗi khác, nên xếp tất cả card vào một cột rồi
				    tất cả vòng tròn vào cột kia thì hai bên lệch hàng nhau và mọi mép
				    ngang bên dưới trượt đi. Cùng một hàng lưới thì chiều cao tự khớp. */}
				<div className="space-y-4 lg:space-y-6">
					{charts.map((chart) => (
						<div key={chart.title} className="grid gap-4 lg:grid-cols-3 lg:gap-6">
							{/* `content-start` và card cao theo nội dung: cột này vẫn cao
							    bằng vòng tròn nên mép ngoài của hàng thẳng, nhưng phần dư
							    dồn xuống dưới thay vì thổi phồng từng card thành những ô
							    trắng rỗng một nửa. Card cuối chiếm nốt cột còn lại khi số
							    card là lẻ, để không hở một lỗ bên cạnh. */}
							<div className="grid content-start gap-4 sm:grid-cols-2 lg:col-span-2 [&>*:last-child:nth-child(odd)]:sm:col-span-2">
								{chart.total.showCard !== false && (
									<StatCard
										label={chart.total.label}
										value={chart.total.value}
										hint={chart.total.hint}
									/>
								)}
								{chart.segments
									.filter((segment) => segment.showCard !== false)
									.map((segment) => (
										<StatCard
											key={segment.label}
											label={segment.label}
											value={segment.value}
											color={segment.color}
											textColor={segment.textColor}
											hint={
												segment.hint
													? `${formatShare(segment.value, chart.total.value)} · ${segment.hint}`
													: formatShare(segment.value, chart.total.value)
											}
										/>
									))}
							</div>

							<Donut chart={chart} />
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function Donut({ chart }: { chart: DonutChart }) {
	const { total, segments, highlightIndex = 0 } = chart;
	const highlight = chart.highlight ?? segments[highlightIndex];

	return (
		<ChartCard title={chart.title} filename={chart.filename} contentClassName="p-4">
			<div className="relative">
				<ResponsiveContainer width="100%" height={200}>
					<PieChart>
						<Pie
							data={segments}
							dataKey="value"
							cx="50%"
							cy="50%"
							innerRadius={60}
							outerRadius={88}
							paddingAngle={0}
							startAngle={90}
							endAngle={-270}
							isAnimationActive={false}
							stroke="none"
						>
							{segments.map((segment) => (
								<Cell key={segment.label} fill={segment.color} />
							))}
						</Pie>
					</PieChart>
				</ResponsiveContainer>

				{/* Đặt bằng HTML thay vì nhãn SVG: chữ nét hơn và tự xuống dòng.
				    `max-w` của nhãn bám theo đường kính lỗ giữa (innerRadius 60 ⇒
				    120px): dài hơn thế thì chữ đè lên chính vành biểu đồ. */}
				{highlight && (
					<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
						<span className="text-3xl font-bold text-foreground">
							{formatShare(highlight.value, total.value)}
						</span>
						<span className="mt-1 max-w-[7rem] text-xs leading-tight text-muted-foreground">
							{highlight.label}
						</span>
					</div>
				)}
			</div>

			<div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
				{segments.map((segment) => (
					<span key={segment.label} className="flex items-center gap-1.5 text-sm">
						<span
							className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
							style={{ backgroundColor: segment.color }}
						/>
						{segment.label}
					</span>
				))}
			</div>
		</ChartCard>
	);
}

interface StatCardProps {
	label: string;
	value: number;
	color?: string;
	textColor?: string;
	hint?: string;
}

function StatCard({ label, value, color, textColor, hint }: StatCardProps) {
	return (
		<Card>
			<CardContent className="p-4">
				<p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
					{color && (
						<span
							className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
							style={{ backgroundColor: color }}
						/>
					)}
					{label}
				</p>
				<p
					className="mt-1 text-2xl font-bold md:text-3xl"
					style={{ color: textColor ?? color ?? "hsl(var(--foreground))" }}
				>
					{value.toLocaleString("vi-VN")}
				</p>
				{hint && <p className="mt-1 text-xs text-muted-foreground md:text-sm">{hint}</p>}
			</CardContent>
		</Card>
	);
}
